"use client"

import React, { useState, useRef, useEffect } from "react"
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import { ArrowLeft, Camera } from "lucide-react"
import Image from "next/image"
import Link from "next/link"
import LogoHeader from "../header/logo-header"
import { convertUrlToFile } from "@/app/components/functions/util/file-url"
import { logger } from "@/lib/logger"
import { REENCODE_MIME, REENCODE_JPEG_QUALITY } from "@/app/components/functions/util/upload-limits"

interface CameraScreenProps {
    onBackToHome: () => void
    onCameraCapture: (file: File) => void
    onNavigateToHome: () => void
}

export default function CameraScreen({ onBackToHome, onCameraCapture, onNavigateToHome }: CameraScreenProps) {
    const [backgroundColor] = useState("#FAF9F7")
    const [textColor] = useState("#4E5562")
    const [fontFamily] = useState("Verdana, sans-serif")
    const [fontSize] = useState(18)
    const [lineSpacing] = useState(1.5)

    const webcamRef = useRef<HTMLVideoElement>(null);
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const [imageSrc, setImageSrc] = useState<string | null>(null);
    const [devices, setDevices] = useState<MediaDeviceInfo[]>([]);
    const [selectedDeviceId, setSelectedDeviceId] = useState("");
    const [cameraStatus, setCameraStatus] = useState<"error" | "loading" | "ready">("loading");
    const [cameraStatusErrorDetail, setCameraStatusErrorDetail] = useState<"Device in use" | "Permission denied" | "NotAllowedError" | "NotFoundError" | "NotReadableError" | "OverconstrainedError" | "">("");

    const mountedRef = useRef(true);
    const currentStreamRef = useRef<MediaStream | null>(null);
    const isStartingRef = useRef(false);

    // Initialize camera devices
    useEffect(() => {
        let ignore = false;
        reviewAllAvailableCamerasAndChooseTheMostAppropriateAsDefaultOne(ignore);

        const handleDeviceChange = () => {
            logger.debug("Camera device change detected");
            reviewAllAvailableCamerasAndChooseTheMostAppropriateAsDefaultOne(ignore);
        };
        navigator.mediaDevices.addEventListener('devicechange', handleDeviceChange);

        return () => {
            ignore = true;
            navigator.mediaDevices.removeEventListener('devicechange', handleDeviceChange);
        };
    }, []);

    // Start camera when device changes
    useEffect(() => {
        if (!selectedDeviceId) return;

        let ignore = false;

        startCamera(ignore);

        return () => {
            ignore = true;
            logger.debug("Cleaning up camera", { deviceId: selectedDeviceId });
            stopCurrentStream();
        };
    }, [selectedDeviceId]);

    // Handle visibility changes
    useEffect(() => {
        const handleVisibility = () => {
            if (document.visibilityState === "visible" && selectedDeviceId) {
                logger.debug("Page visible, restarting camera");
                // Force restart by clearing and re-setting
                const deviceId = selectedDeviceId;
                setSelectedDeviceId("");
                setTimeout(() => setSelectedDeviceId(deviceId), 100);
            }
        };
        document.addEventListener("visibilitychange", handleVisibility);

        const handlePageShow = (event: PageTransitionEvent) => {
            if (event.persisted && selectedDeviceId) {
                logger.debug("Page restored from bfcache");
                const deviceId = selectedDeviceId;
                setSelectedDeviceId("");
                setTimeout(() => setSelectedDeviceId(deviceId), 100);
            }
        };
        window.addEventListener("pageshow", handlePageShow);

        return () => {
            document.removeEventListener("visibilitychange", handleVisibility);
            window.removeEventListener("pageshow", handlePageShow);
        };
    }, [selectedDeviceId]);

    // Cleanup on unmount
    useEffect(() => {
        mountedRef.current = true;
        logger.debug("Camera screen mounted");
        return () => {
            logger.debug("Camera screen unmounting");
            mountedRef.current = false;
            stopCurrentStream();
        };
    }, []);

    function isBackCamera(label = "") {
        const lower = label.toLowerCase();
        return (
            lower.includes("back") ||
            lower.includes("facing back") ||
            lower.includes("rear") ||
            lower.includes("tylny") ||
            lower.includes("tylna") ||
            lower.includes("environment")
        );
    }

    async function startCamera(ignore: boolean) {
        if (isStartingRef.current) { // Prevent overlapping starts
            logger.debug("Camera start already in progress");
            return;
        }
        isStartingRef.current = true;
        logger.info("Starting camera", { deviceId: selectedDeviceId });

        stopCurrentStream();
        if (webcamRef.current) {
            webcamRef.current.srcObject = null;
        }
        await new Promise(resolve => setTimeout(resolve, 250));

        if (ignore) {
            isStartingRef.current = false;
            return;
        }

        setCameraStatus("loading");
        try {
            logger.debug("Requesting camera with deviceId");
            await getCamera(ignore, CameraMode.DEVICE_ID);
        } catch (err) {
            logger.debug("Trying fallback facingMode: environment");
            await getCamera(ignore, CameraMode.FACING_MODE);
        } finally {
            isStartingRef.current = false;
        }
    };

    async function reviewAllAvailableCamerasAndChooseTheMostAppropriateAsDefaultOne(ignore: boolean, retryCount = 0) {
        const maxRetries = 3;
        const retryDelay = 300; // ms
        
        try {
            logger.debug("Initializing camera devices", { retryCount });

            const tempStream = await navigator.mediaDevices.getUserMedia({ video: true });
            tempStream.getTracks().forEach((t) => t.stop());

            const allDevices = await navigator.mediaDevices.enumerateDevices();
            const videoDevices = allDevices.filter((d) => d.kind === "videoinput");

            if (ignore) return;

            logger.info("Camera devices found", { count: videoDevices.length });
            setDevices(videoDevices);

            if (videoDevices.length > 0) {
                const backCam = videoDevices.find((d) => isBackCamera(d.label));
                const defaultDevice = backCam || videoDevices[0];
                logger.debug("Setting default camera", { label: defaultDevice.label, isBack: !!backCam });
                setSelectedDeviceId(defaultDevice.deviceId);
            }
        } catch (err: unknown) {
            const errorMsg = err instanceof Error ? err.message : String(err);
            logger.error("Camera initialization failed", { error: err instanceof Error ? err : errorMsg, attempt: retryCount + 1, maxAttempts: maxRetries + 1 });
            
            // Retry if permission was just granted (race condition with navigation)
            if (retryCount < maxRetries && !ignore) {
                logger.debug("Retrying camera initialization", { delayMs: retryDelay });
                await new Promise(resolve => setTimeout(resolve, retryDelay));
                if (!ignore) {
                    return reviewAllAvailableCamerasAndChooseTheMostAppropriateAsDefaultOne(ignore, retryCount + 1);
                }
            }
            
            if (!ignore) {
                setCameraStatus("error");
                setCameraStatusErrorDetail(err instanceof Error ? err.message : String(err));
            }
        }
    }

    async function getCamera(ignore: boolean, cameraMode: CameraMode) {
        const videoConstraints: MediaTrackConstraints = {
            width: { ideal: 1280 },
            height: { ideal: 720 },
        };
        if (cameraMode == CameraMode.DEVICE_ID) {
            videoConstraints.deviceId = { exact: selectedDeviceId };
        } else if (cameraMode == CameraMode.FACING_MODE) {
            videoConstraints.facingMode = "environment";
        }

        try {
            const stream = await navigator.mediaDevices.getUserMedia({
                video: videoConstraints,
                audio: false,
            });

            if (ignore) {
                stream.getTracks().forEach(track => track.stop());
                isStartingRef.current = false;
                return;
            }

            currentStreamRef.current = stream;

            if (webcamRef.current) {
                webcamRef.current.srcObject = stream;
                try {
                    await webcamRef.current.play();
                } catch (playErr) {
                    logger.warn("Camera play() failed", { error: playErr instanceof Error ? playErr.message : String(playErr) });
                }
            }

            if (ignore) {
                isStartingRef.current = false;
                return;
            }

            const settings = stream.getVideoTracks()[0].getSettings();
            logger.info("Camera started", { width: settings.width, height: settings.height });
            setCameraStatus("ready");

            return stream;
        } catch (err: unknown) {
            const errorMsg = err instanceof Error ? err.message : String(err);
            
            if (cameraMode == CameraMode.DEVICE_ID) {
                logger.warn("Camera with deviceId failed", { error: errorMsg });
            } else if (cameraMode == CameraMode.FACING_MODE) {
                logger.error("All camera attempts failed", { error: err instanceof Error ? err : errorMsg });
            }
            if (ignore) {
                isStartingRef.current = false;
                return;
            } else {
                setCameraStatus("error");
                setCameraStatusErrorDetail(err instanceof Error ? err.message : String(err));
            }
            throw err;
        }

    }

    function stopCurrentStream() {
        const oldStream = currentStreamRef.current;
        if (oldStream) {
            logger.debug("Stopping camera stream");
            oldStream.getTracks().forEach((track) => track.stop());
            currentStreamRef.current = null;
        }
    }

    const handleCameraCapture = async () => {
        const video = webcamRef.current;
        const canvas = canvasRef.current;

        if (!video || !canvas) return;

        const ctx = canvas.getContext("2d");
        if (!ctx) return;

        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;

        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

        /*
         * JPEG, not PNG. The file has always been named photo.jpg while carrying
         * PNG bytes - DYS-042 - and the name was the least of it: a lossless
         * encode of a camera frame is several times the size of the same frame as
         * JPEG, on an upload path with a 4 MB ceiling and an audience on mobile
         * data. Nothing downstream wanted PNG, and the server re-encodes to JPEG
         * anyway after its own downscale.
         */
        const screenshot = canvas.toDataURL(REENCODE_MIME, REENCODE_JPEG_QUALITY);

        setImageSrc(screenshot);
        if (screenshot) {
            const file = await convertUrlToFile(screenshot, 'photo.jpg');
            logger.info("Photo captured", { width: canvas.width, height: canvas.height, fileSize: file.size });
            onCameraCapture(file);
        }
    };

    enum CameraMode {
        DEVICE_ID = "device_id",
        FACING_MODE = "facing_mode",
    }

    return (
        <div className="flex flex-col h-screen" style={{ backgroundColor: backgroundColor }}>
            <LogoHeader 
                onLogoClick={onNavigateToHome}
                onBackClick={onBackToHome}
                showBackButton={true}
                title="Aparat"
            />

            <div className="flex-1 relative">
                <div className="flex-1 flex flex-col">
                    <div className="flex-1">
                        <div className="w-full max-w-4xl mx-auto relative">
                            <Card className="w-full min-h-[500px] border-none rounded-lg overflow-hidden shadow-md">
                                <CardContent
                                    className="p-0 relative"
                                    style={{
                                        color: textColor,
                                        fontSize: `${fontSize}px`,
                                        lineHeight: lineSpacing,
                                        fontFamily: fontFamily,
                                    }}
                                >
                                    {/* Video container that takes full available space minus button area */}
                                    <div className="relative h-[calc(100vh-64px-100px)] max-md:h-[calc(100vh-64px-80px)]">
                                        <video
                                            ref={webcamRef}
                                            autoPlay
                                            playsInline
                                            muted
                                            className="absolute inset-0 w-full h-full object-cover"
                                        />

                                        {cameraStatus === "loading" && (
                                            <div className="absolute inset-0 flex items-center justify-center bg-black/70 text-white">
                                                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-white"></div>
                                            </div>
                                        )}

                                        {cameraStatus === "error" && (
                                            <div className="absolute inset-0 flex flex-col items-center justify-center ">
                                                <p className="font-semibold mb-2">Nie uzyskano dostępu do kamery</p>
                                                {cameraStatusErrorDetail === "Permission denied" && (
                                                    <p className="font-normal text-sm">
                                                    <br/>Twoja przeglądarka nie pozwala na użycie kamery. 
                                                    <br/>Spróbuj włączyć dostęp do kamery w ustawieniach przeglądarki.
                                                    </p>
                                                )}
                                                {cameraStatusErrorDetail === "Device in use" && (
                                                    <p className="font-normal text-sm">Kamera jest używana przez inną aplikację</p>
                                                )}
                                            </div>
                                        )}
                                    </div>

                                    {/* Desktop: button below video */}
                                    <div className="hidden md:flex justify-center p-4">
                                        <Button
                                            onClick={handleCameraCapture}
                                            className="bg-[rgba(85,54,109,1)] hover:bg-[rgba(85,54,109,0.9)] rounded-full text-white shadow-lg"
                                            size="lg"
                                            disabled={cameraStatus !== "ready"}
                                        >
                                            <Camera className="h-4 w-4 mr-1" />
                                            Zrób zdjęcie tekstu
                                        </Button>
                                    </div>
                                    <canvas ref={canvasRef} style={{ display: "none" }} />
                                </CardContent>
                            </Card>
                        </div>
                    </div>
                </div>
            </div>

            {/* Mobile: Fixed button at bottom of screen - always visible */}
            <div className="md:hidden fixed bottom-0 left-0 right-0 bg-black/5 backdrop-blur-[2px] p-4 flex justify-center z-50">
                <Button
                    onClick={handleCameraCapture}
                    className="bg-[rgba(85,54,109,1)] hover:bg-[rgba(85,54,109,0.9)] rounded-full text-white shadow-lg"
                    size="lg"
                    disabled={cameraStatus !== "ready"}
                >
                    <Camera className="h-4 w-4 mr-1" />
                    Zrób zdjęcie tekstu
                </Button>
            </div>
        </div>
    )
}